import {
  TableCellType,
  TableHeader,
} from "../../../../../components/custom-table/table";
import { AuthStatusOptionModel } from "../../../../../data-model/authStatusOptions";
import { Hit } from "../../../../../data-model/postgresResponseForMedicalPaOrders";
import {
  DocWriteBackStatus,
  EVWriteBackStatus,
} from "../../../../../enums/evBvWriteBackStatus";
import { getBadgePropsForAuthStatus } from "../../../../../enums/medicalPaOrdersAuthStatus";
import { formatDateToUTCWithFormat } from "../../../../../utils/formatDate";
import { generateFullName } from "../../../../../utils/generateOrderDataForTable";
import { rotateLastSegmentFirst } from "../../../../../utils/getFullNameOfTheProvider";
import { capitalizeString } from "../../../../../utils/stringModifications";

/** Column key identifiers for the external PA orders Algolia-powered table. */
export enum AllPaOrdersForExternalTableByAlgoliaColumnKeys {
  PATIENT_DETAILS = "patientDetails",
  CREATED_AT = "createdAt",
  DATE_OF_WORK = "dateOfWork",
  PLAN_DETAILS = "planDetails",
  MEDICATION = "medication",
  PROVIDER = "provider",
  DATE_OF_SERVICE = "dateOfService",
  BO_VALUE = "boValue",
  AUTH_VERIFICATION = "authVerification",
  RPA_STATUS = "rpaStatus",
  DOCUMENTS_LIST = "documentsList",
  ID = "id",
  ROW_DATA = "rowData",
}

/** Table header configuration for the external PA orders Algolia table. */
export const AllPaOrdersForExternalTableByAlgoliaHeaders: TableHeader[] = [
  {
    label: "Patient Name",
    key: AllPaOrdersForExternalTableByAlgoliaColumnKeys.PATIENT_DETAILS,
    order: 1,
    width: 15,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
  },
  {
    label: "Date of Creation",
    key: AllPaOrdersForExternalTableByAlgoliaColumnKeys.CREATED_AT,
    order: 2,
    width: 8,
    sortable: true,
    type: TableCellType.DATE,
  },
  {
    label: "Date of Work",
    key: AllPaOrdersForExternalTableByAlgoliaColumnKeys.DATE_OF_WORK,
    order: 3,
    width: 8,
    sortable: true,
    type: TableCellType.DATE,
  },
  {
    label: "Plan Details",
    key: AllPaOrdersForExternalTableByAlgoliaColumnKeys.PLAN_DETAILS,
    order: 4,
    width: 10,
    sortable: true,
    type: TableCellType.MULTILINE,
    subKey: "mainText",
  },
  {
    label: "Medication",
    key: AllPaOrdersForExternalTableByAlgoliaColumnKeys.MEDICATION,
    order: 5,
    width: 15,
    sortable: true,
    type: TableCellType.STRING_WITH_CUSTOM_TOOLTIP,
  },
  {
    label: "Provider",
    key: AllPaOrdersForExternalTableByAlgoliaColumnKeys.PROVIDER,
    order: 6,
    width: 10,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "DOS",
    key: AllPaOrdersForExternalTableByAlgoliaColumnKeys.DATE_OF_SERVICE,
    order: 7,
    width: 8,
    sortable: true,
    type: TableCellType.DATE,
  },
  {
    label: "BO Value",
    key: AllPaOrdersForExternalTableByAlgoliaColumnKeys.BO_VALUE,
    order: 8,
    width: 9,
    type: TableCellType.BADGE,
  },
  {
    label: "Auth Status",
    key: AllPaOrdersForExternalTableByAlgoliaColumnKeys.AUTH_VERIFICATION,
    order: 9,
    width: 9,
    type: TableCellType.BADGE_WITH_I,
  },
  {
    label: "",
    key: AllPaOrdersForExternalTableByAlgoliaColumnKeys.RPA_STATUS,
    order: 10,
    width: 8,
    type: TableCellType.RPA_STATUS_FOR_EXTERNAL,
  },
];

/**
 * Transforms Postgres medical PA order hit records into table-ready row data for the external PA orders table.
 * Formats patient names, dates, plan details, auth status badges, BO values, and RPA status.
 *
 * @param data - Hit objects from the Postgres medical PA orders search response
 * @param authStatusOptions - Auth status options for badge color/text configuration
 * @returns Array of table row objects keyed by AllPaOrdersForExternalTableByAlgoliaColumnKeys
 */
export const generateAllPaOrdersForExternalByAlgoliaTableData = (
  data: Hit[],
  authStatusOptions: AuthStatusOptionModel[],
) => {
  return data.map((item) => {
    const authStatus = getBadgePropsForAuthStatus(
      authStatusOptions,
      item?.auth_status,
    );
    const { displayText, tooltipText } = {
      displayText: item?.regimen_name ?? "",
      tooltipText: item?.regimen_name ?? "",
    };
    return {
      [AllPaOrdersForExternalTableByAlgoliaColumnKeys.PATIENT_DETAILS]: {
        mainText: capitalizeString(
          generateFullName(item?.first_name ?? "", "", item?.last_name ?? ""),
        ),
        secondaryText: `MRN : ${item?.patient_id ?? ""}`,
      },
      [AllPaOrdersForExternalTableByAlgoliaColumnKeys.DATE_OF_SERVICE]:
        formatDateToUTCWithFormat(
          item?.date_of_service_iso ?? "",
          "MM/DD/YYYY",
          "--",
        ),
      [AllPaOrdersForExternalTableByAlgoliaColumnKeys.PLAN_DETAILS]: {
        mainText: item?.primary_payer_name ?? "",
        secondaryText: item?.primary_member_id ?? "",
      },
      [AllPaOrdersForExternalTableByAlgoliaColumnKeys.MEDICATION]: {
        displayText,
        tooltipText,
      },
      [AllPaOrdersForExternalTableByAlgoliaColumnKeys.ROW_DATA]:
        JSON.stringify(item),
      [AllPaOrdersForExternalTableByAlgoliaColumnKeys.ID]: item?.id ?? "",
      [AllPaOrdersForExternalTableByAlgoliaColumnKeys.CREATED_AT]:
        formatDateToUTCWithFormat(
          item?.created_at_iso ?? "",
          "MM/DD/YYYY",
          "--",
        ),
      [AllPaOrdersForExternalTableByAlgoliaColumnKeys.DATE_OF_WORK]:
        item?.date_of_work_iso ?? null,
      [AllPaOrdersForExternalTableByAlgoliaColumnKeys.AUTH_VERIFICATION]: {
        text: authStatus?.text ?? "",
        color: authStatus?.textColor ?? "",
        bgColor: authStatus?.bgColor ?? "",
        showInfoIcon:
          authStatus?.id === "denied" || authStatus?.id === "denied_by_risa",
        //   displayText: getToolTipTextForAuthStatus(medicalPaOrder),
      },
      [AllPaOrdersForExternalTableByAlgoliaColumnKeys.BO_VALUE]: {
        text: item?.bo_status ?? "Required",
        color: "#0f0f0f",
        bgColor: "#f0f0f0",
      },
      [AllPaOrdersForExternalTableByAlgoliaColumnKeys.RPA_STATUS]: {
        id: item?.id ?? "",
        documentUploadStatus:
          item?.document_upload_status ?? DocWriteBackStatus.NOT_INITIATED,
        evWriteBackStatus:
          item?.ev_write_back_status ?? EVWriteBackStatus.NOT_INITIATED,
        masterAuthStatus: item?.master_auth_status ?? "",
      },
      [AllPaOrdersForExternalTableByAlgoliaColumnKeys.PROVIDER]:
        rotateLastSegmentFirst(item?.practitioner_name ?? ""),
    };
  });
};
